"use client"

import { useCallback, useEffect, useRef, useState } from 'react'
import { useVoiceRecorder, MicrophoneAccessError } from '@/hooks/use-voice-recorder'
import { transcribeAudio, TranscribeError } from '@/lib/voice/transcribe'
import { claimVoiceSession, releaseVoiceSession } from '@/lib/voice/voice-session'

export type VoiceInputState = 'idle' | 'recording' | 'transcribing' | 'error'

/** Мягкий лимит записи; длинная запись (больше 25 МБ провайдера) распознаётся сервером по кускам */
export const VOICE_MAX_DURATION_SEC = 60 * 60

export interface UseVoiceInputOptions {
  /** Вставить распознанный текст. Обязателен. */
  onInsert: (text: string) => void
  onStateChange?: (state: VoiceInputState) => void
  maxDurationSec?: number
}

export interface VoiceInputApi {
  state: VoiceInputState
  errorMessage: string | null
  elapsedSec: number
  getLevel: () => number
  /** idle → recording */
  startRecording: () => void
  /** recording → transcribing → idle | error */
  stopAndTranscribe: () => void
  /** recording → idle (запись отбрасывается) */
  cancelRecording: () => void
  /** transcribing → idle (abort запроса, аудио отбрасывается) */
  skip: () => void
  /** error → transcribing (повторная отправка сохранённого Blob) */
  retry: () => void
  /** error → idle */
  dismissError: () => void
}

function describeError(error: unknown): string {
  if (error instanceof MicrophoneAccessError) return error.message
  if (error instanceof TranscribeError) {
    if (error.status === 401) return 'Требуется авторизация'
    if (error.status === 0) return 'Нет соединения'
    return error.message
  }
  return 'Не удалось распознать речь'
}

/**
 * Вся логика голосового ввода: запись → отправка → вставка. Кнопка — только вид.
 */
export function useVoiceInput({ onInsert, onStateChange, maxDurationSec = VOICE_MAX_DURATION_SEC }: UseVoiceInputOptions): VoiceInputApi {
  const [state, setStateRaw] = useState<VoiceInputState>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const stateRef = useRef<VoiceInputState>('idle')
  const onInsertRef = useRef(onInsert)
  const onStateChangeRef = useRef(onStateChange)
  useEffect(() => { onInsertRef.current = onInsert }, [onInsert])
  useEffect(() => { onStateChangeRef.current = onStateChange }, [onStateChange])

  const sessionIdRef = useRef<number | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const lastBlobRef = useRef<Blob | null>(null)
  const mountedRef = useRef(true)

  const setState = useCallback((next: VoiceInputState) => {
    stateRef.current = next
    setStateRaw(next)
    onStateChangeRef.current?.(next)
  }, [])

  const releaseSession = useCallback(() => {
    if (sessionIdRef.current !== null) {
      releaseVoiceSession(sessionIdRef.current)
      sessionIdRef.current = null
    }
  }, [])

  const stopAndTranscribeRef = useRef<() => void>(() => {})

  const recorder = useVoiceRecorder({
    maxDurationSec,
    onAutoStop: () => stopAndTranscribeRef.current(),
  })
  const { start: recorderStart, stop: recorderStop, cancel: recorderCancel } = recorder

  const runTranscription = useCallback(async (blob: Blob) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    lastBlobRef.current = blob
    setErrorMessage(null)
    setState('transcribing')

    try {
      const text = await transcribeAudio(blob, controller.signal)
      if (controller.signal.aborted || !mountedRef.current) return
      abortRef.current = null
      lastBlobRef.current = null
      releaseSession()
      setState('idle')
      const trimmed = text.trim()
      if (trimmed) onInsertRef.current(trimmed)
    } catch (error) {
      if (controller.signal.aborted || !mountedRef.current) return
      abortRef.current = null
      setErrorMessage(describeError(error))
      setState('error')
    }
  }, [releaseSession, setState])

  const cancelRecording = useCallback(() => {
    recorderCancel()
    abortRef.current?.abort()
    abortRef.current = null
    lastBlobRef.current = null
    releaseSession()
    setErrorMessage(null)
    if (mountedRef.current) setState('idle')
  }, [recorderCancel, releaseSession, setState])

  const startRecording = useCallback(async () => {
    if (stateRef.current !== 'idle') return
    // Отмена предыдущей активной записи (другого поля) происходит внутри claimVoiceSession
    sessionIdRef.current = claimVoiceSession(() => {
      // Нас вытеснила другая запись: в recording — отбрасываем, в transcribing — не мешаем
      if (stateRef.current === 'recording') {
        recorderCancel()
        sessionIdRef.current = null
        setState('idle')
      }
    })
    setErrorMessage(null)
    setState('recording')
    try {
      await recorderStart()
    } catch (error) {
      releaseSession()
      if (!mountedRef.current) return
      setErrorMessage(describeError(error))
      setState('error')
    }
  }, [recorderCancel, recorderStart, releaseSession, setState])

  const stopAndTranscribe = useCallback(async () => {
    if (stateRef.current !== 'recording') return
    let blob: Blob
    try {
      blob = await recorderStop()
    } catch (error) {
      releaseSession()
      if (!mountedRef.current) return
      setErrorMessage(describeError(error))
      setState('error')
      return
    }
    if (!mountedRef.current) return
    if (blob.size === 0) {
      releaseSession()
      setState('idle')
      return
    }
    void runTranscription(blob)
  }, [recorderStop, releaseSession, runTranscription, setState])
  useEffect(() => { stopAndTranscribeRef.current = () => { void stopAndTranscribe() } }, [stopAndTranscribe])

  const skip = useCallback(() => {
    if (stateRef.current !== 'transcribing') return
    abortRef.current?.abort()
    abortRef.current = null
    lastBlobRef.current = null
    releaseSession()
    setState('idle')
  }, [releaseSession, setState])

  const retry = useCallback(() => {
    if (stateRef.current !== 'error') return
    const blob = lastBlobRef.current
    if (!blob) {
      // Ошибка была на этапе доступа к микрофону — пробуем записать заново
      setErrorMessage(null)
      setState('idle')
      void startRecording()
      return
    }
    void runTranscription(blob)
  }, [runTranscription, setState, startRecording])

  const dismissError = useCallback(() => {
    if (stateRef.current !== 'error') return
    lastBlobRef.current = null
    releaseSession()
    setErrorMessage(null)
    setState('idle')
  }, [releaseSession, setState])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortRef.current?.abort()
      if (sessionIdRef.current !== null) releaseVoiceSession(sessionIdRef.current)
    }
  }, [])

  return {
    state,
    errorMessage,
    elapsedSec: recorder.elapsedSec,
    getLevel: recorder.getLevel,
    startRecording: () => { void startRecording() },
    stopAndTranscribe: () => { void stopAndTranscribe() },
    cancelRecording,
    skip,
    retry,
    dismissError,
  }
}
